import CardFillDetailDialog from '@/components/client/card-fill-detail-dialog';
import CopyablePhone from '@/components/copyable-phone';
import PhoneStatusBadge from '@/components/client/phone-status-badge';
import { Button } from '@/components/ui/button';
import type { ICard, ICardFillJob, IPhone } from '@/entities/client.entity';
import { requestCardBalance } from '@/lib/card-fill-actions';
import {
	formatBalanceFetchedShort,
	getLatestPhoneLookupJob,
	getPhoneLookupStatusLabel,
	hasActiveBalanceFetchJob,
	hasActiveCardJob,
	isCardBalanceFetchReady,
	isJobActive,
} from '@/lib/card-fill-meta';
import { formatCardNumber, resolveCardDisplayNumber } from '@/lib/format-card';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';
import {
	AsteriskIcon,
	BanknoteIcon,
	CalendarIcon,
	ClockIcon,
	CreditCardIcon,
	GlobeIcon,
	InfoIcon,
	Loader2Icon,
	RefreshCwIcon,
	RotateCcwIcon,
} from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';

const TREE_ICON_SIZE = 13;
const balanceMoney = new Intl.NumberFormat('en-US', {
	style: 'decimal',
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

type TreeScaffold = 'branch' | 'branchEnd';

function scaffoldChar(scaffold: TreeScaffold): string {
	return scaffold === 'branchEnd' ? '╰' : '│';
}

interface TreeLineProps {
	scaffold: TreeScaffold;
	icon: LucideIcon;
	children: ReactNode;
	className?: string;
	actions?: ReactNode;
}

export function TreeLine({ scaffold, icon: Icon, children, className, actions }: TreeLineProps) {
	return (
		<div className={cn('flex min-w-0 items-center gap-1.5', className)}>
			<span className='w-4 shrink-0 font-mono text-xs text-muted-foreground/70 tabular-nums select-none'>
				{scaffoldChar(scaffold)}
			</span>
			<Icon size={TREE_ICON_SIZE} className='shrink-0 text-muted-foreground' aria-hidden />
			<div className='min-w-0 flex-1'>{children}</div>
			{actions ? <div className='flex shrink-0 items-center gap-0.5'>{actions}</div> : null}
		</div>
	);
}

function formatTreeBalance(balance: number): string {
	return `${balanceMoney.format(balance)} sum`;
}

interface CardTreeBlockProps {
	card: ICard;
	phones?: IPhone[];
	cardFillJobs?: ICardFillJob[];
	onCopy: (text: string) => void;
}

export function CardTreeBlock({ card, phones, cardFillJobs, onCopy }: CardTreeBlockProps) {
	const [detailOpen, setDetailOpen] = useState(false);
	const displayNumber = formatCardNumber(card);
	const copyValue = resolveCardDisplayNumber(card);
	const hasDate = Boolean(card.date?.trim());
	const hasBalance = card.balance != null;
	const hasFetchedAt = Boolean(card.balanceFetchedAt);
	const isActive = hasActiveCardJob(cardFillJobs, card.id);
	const balanceRefreshing = hasActiveBalanceFetchJob(cardFillJobs, card.id);
	const canRefreshBalance = isCardBalanceFetchReady(card);

	const lines: Array<{ scaffold: TreeScaffold; key: string }> = [];
	lines.push({ scaffold: 'branch', key: 'pan' });
	if (hasDate) lines.push({ scaffold: 'branch', key: 'date' });
	if (hasBalance) lines.push({ scaffold: 'branch', key: 'balance' });
	if (hasFetchedAt) {
		lines.push({ scaffold: 'branchEnd', key: 'time' });
	} else if (lines.length > 0) {
		lines[lines.length - 1] = { ...lines[lines.length - 1], scaffold: 'branchEnd' };
	}

	function handleNumberCopy(e: MouseEvent) {
		e.stopPropagation();
		if (copyValue !== '—') onCopy(copyValue);
	}

	function handleBalanceCopy(e: MouseEvent) {
		e.stopPropagation();
		if (card.balance != null) onCopy(String(card.balance));
	}

	const cardActions = (
		<>
			{isActive ? <Loader2Icon size={12} className='animate-spin text-chart-3' /> : null}
			{canRefreshBalance ? (
				<Button
					type='button'
					variant='ghost'
					size='sm'
					className='h-6 w-6 p-0'
					disabled={balanceRefreshing}
					onClick={e => {
						e.stopPropagation();
						requestCardBalance(card.id);
					}}
				>
					{balanceRefreshing ? (
						<Loader2Icon size={12} className='animate-spin' />
					) : (
						<RefreshCwIcon size={12} />
					)}
				</Button>
			) : null}
			<Button
				type='button'
				variant='ghost'
				size='sm'
				className='h-6 w-6 p-0'
				onClick={e => {
					e.stopPropagation();
					setDetailOpen(true);
				}}
			>
				<InfoIcon size={12} />
			</Button>
		</>
	);

	return (
		<div className='border-l border-border/60 ml-2 pl-1.5 flex flex-col gap-0.5'>
			{lines.map(line => {
				if (line.key === 'pan') {
					return (
						<TreeLine
							key={line.key}
							scaffold={line.scaffold}
							icon={CreditCardIcon}
							actions={cardActions}
						>
							<button
								type='button'
								className='text-left text-sm font-semibold font-mono break-all hover:bg-accent/60 active:bg-accent rounded-xs px-0.5 cursor-pointer select-none'
								onClick={handleNumberCopy}
							>
								{displayNumber}
							</button>
						</TreeLine>
					);
				}
				if (line.key === 'date') {
					return (
						<TreeLine key={line.key} scaffold={line.scaffold} icon={CalendarIcon}>
							<span className='text-sm text-muted-foreground tabular-nums'>{card.date}</span>
						</TreeLine>
					);
				}
				if (line.key === 'balance') {
					return (
						<TreeLine key={line.key} scaffold={line.scaffold} icon={BanknoteIcon}>
							<button
								type='button'
								className='text-sm font-semibold tabular-nums text-destructive hover:bg-accent/60 active:bg-accent rounded-xs px-0.5 cursor-pointer select-none'
								onClick={handleBalanceCopy}
							>
								{formatTreeBalance(card.balance ?? 0)}
							</button>
						</TreeLine>
					);
				}
				return (
					<TreeLine key={line.key} scaffold={line.scaffold} icon={ClockIcon}>
						<span className='text-xs text-muted-foreground tabular-nums'>
							{formatBalanceFetchedShort(card.balanceFetchedAt)}
						</span>
					</TreeLine>
				);
			})}

			<CardFillDetailDialog
				target={{ mode: 'card', card, phones }}
				jobs={cardFillJobs}
				open={detailOpen}
				onOpenChange={setDetailOpen}
			/>
		</div>
	);
}

const RESOLVE_REASON_LABEL: Record<string, string> = {
	unknown_operator: 'Неизвестный оператор',
	skipped_perfectum: 'Perfectum: USSD недоступен',
	ussd_timeout: 'Таймаут USSD',
	ussd_failure: 'Ошибка USSD',
	parse_failed: 'Не удалось распознать номер',
	max_attempts: 'Исчерпаны попытки',
	ussd_multi_candidate: 'USSD: выбран первый из нескольких номеров',
};

function buildPhoneHeaderParts(phone: IPhone): string[] {
	const parts: string[] = [phone.name];
	if (phone.simSlotIndex != null) {
		parts[0] = `${phone.name} (${phone.simSlotIndex})`;
	}
	if (phone.isDefault) parts.push('main');
	if (phone.countryIso) parts.push(phone.countryIso.toUpperCase());
	return parts;
}

interface PhoneSimHeaderProps {
	phone: IPhone;
	clientId?: string;
	cardFillJobs?: ICardFillJob[];
	onCopy: (text: string) => void;
	onUssd: () => void;
	onInfo: () => void;
	onRetryResolve?: () => void;
}

export function PhoneSimHeader({
	phone,
	cardFillJobs,
	onCopy,
	onUssd,
	onInfo,
	onRetryResolve,
}: PhoneSimHeaderProps) {
	const headerParts = buildPhoneHeaderParts(phone);
	const latestLookup = getLatestPhoneLookupJob(cardFillJobs, phone.id);
	const lookupRunning = isJobActive(latestLookup);
	const lookupStatusLabel = getPhoneLookupStatusLabel(cardFillJobs, phone.id);

	return (
		<div className='flex items-start justify-between gap-2 min-w-0'>
			<div className='min-w-0 flex-1'>
				<div className='flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0'>
					<GlobeIcon size={14} className='shrink-0 text-muted-foreground' aria-hidden />
					<span className='text-sm font-medium break-all'>
						{headerParts.join(' | ')}
						{phone.number ? (
							<>
								{' | '}
								<CopyablePhone
									value={phone.number}
									onCopy={onCopy}
									className='inline font-semibold text-chart-3 hover:bg-accent/60 active:bg-accent rounded-xs px-0.5'
								/>
							</>
						) : null}
					</span>
				</div>

				{!phone.number && phone.status != null ? (
					<div className='mt-1.5 flex flex-col gap-1 pl-5'>
						<PhoneStatusBadge status={phone.status} />
						{phone.status === 3 && phone.numberResolveReason ? (
							<p className='text-[11px] text-destructive/90'>
								{RESOLVE_REASON_LABEL[phone.numberResolveReason] ?? phone.numberResolveReason}
							</p>
						) : null}
						{phone.status === 3 && onRetryResolve ? (
							<Button
								variant='outline'
								size='sm'
								className='h-7 w-fit px-2 text-xs'
								type='button'
								onClick={onRetryResolve}
							>
								<RotateCcwIcon size={12} className='mr-1' />
								Повторить
							</Button>
						) : null}
					</div>
				) : null}

				{phone.number && lookupStatusLabel ? (
					<p
						className={cn(
							'mt-1 pl-5 text-[11px]',
							latestLookup?.status === 'failed'
								? 'text-destructive/90'
								: lookupRunning
									? 'text-chart-3'
									: 'text-muted-foreground',
						)}
					>
						Вбив: {lookupStatusLabel}
					</p>
				) : null}

				{phone.number && phone.numberResolveReason === 'ussd_multi_candidate' ? (
					<p className='mt-1 pl-5 text-[11px] text-amber-600 dark:text-amber-500'>
						{RESOLVE_REASON_LABEL.ussd_multi_candidate}
					</p>
				) : null}
			</div>

			<div className='flex shrink-0 gap-1'>
				<Button variant='ghost' size='sm' className='h-7 px-2 text-xs' type='button' onClick={onUssd}>
					<AsteriskIcon size={13} />
					USSD
				</Button>
				<Button variant='ghost' size='sm' className='h-7 px-2 text-xs' type='button' onClick={onInfo}>
					<InfoIcon size={13} />
				</Button>
			</div>
		</div>
	);
}

export function EmptyPhoneCardsTree() {
	return (
		<div className='border-l border-border/60 ml-2 pl-1.5 mt-1'>
			<TreeLine scaffold='branchEnd' icon={CreditCardIcon}>
				<span className='text-sm text-muted-foreground'>Пусто</span>
			</TreeLine>
		</div>
	);
}
